/**
 * BrainPanel — the main Brain shell.
 *
 * Used in two modes:
 *   - As a LEFT-side drawer toggled from Dashboard.tsx (default, mobile-friendly bottom-sheet at <md)
 *   - As the body of the /#brain full-screen route via the <Brain> page
 *
 * Composition: ThreadList rail + ChatStream + ChatInput, with a header carrying
 * close/expand/new-thread actions and a model selector.
 */
import { useEffect } from 'react';
import { X, Maximize2, Sparkles, History as HistoryIcon, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useBrain } from '../../contexts/BrainContext';
import { useTenant } from '../../contexts/TenantContext';
import { ThreadList } from './ThreadList';
import { ChatStream } from './ChatStream';
import { ChatInput } from './ChatInput';
import { useState } from 'react';

interface BrainPanelProps {
  /** When true, render as fullscreen page; when false, render as drawer. */
  fullscreen?: boolean;
  /** Drawer mode only — fired when user clicks ✕. */
  onClose?: () => void;
}

export function BrainPanel({ fullscreen = false, onClose }: BrainPanelProps) {
  const { refreshThreads, error } = useBrain();
  const { tenant } = useTenant();
  const [historyOpen, setHistoryOpen] = useState(true);

  useEffect(() => { refreshThreads(); }, [refreshThreads]);

  return (
    <div className={`flex h-full flex-col bg-neutral-950 ${fullscreen ? '' : 'border-l border-white/10'}`}>
      <header className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-purple-500 text-white">
          <Sparkles className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-neutral-100 truncate">Velocity 9 Brain</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 truncate">{tenant?.name ?? 'Tenant'}</div>
        </div>
        <button
          type="button"
          onClick={() => setHistoryOpen((o) => !o)}
          className="rounded-md p-1.5 text-neutral-400 transition hover:bg-white/5 hover:text-neutral-100"
          title={historyOpen ? 'Hide history' : 'Show history'}
        >
          {historyOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
        </button>
        {!fullscreen && (
          <a
            href="#brain"
            className="rounded-md p-1.5 text-neutral-400 transition hover:bg-white/5 hover:text-neutral-100"
            title="Expand to full screen"
          >
            <Maximize2 className="h-4 w-4" />
          </a>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-neutral-400 transition hover:bg-white/5 hover:text-neutral-100"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </header>

      {error && (
        <div className="border-b border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-[11px] text-amber-200">
          {error}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {historyOpen && <ThreadList />}
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <ChatStream />
          </div>
          <ChatInput />
        </div>
      </div>
    </div>
  );
}
